import React, { useState } from 'react'
import { Button, Dropdown } from 'antd'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { Icon } from '@iconify/react'

import CreateTaskCard from '../CreateTaskCard'
import { TaskCard } from '../../ProjectTaskList'
import sectionStyles from './SprintSection.module.css'

const isPendingCreateTask = (task) =>
    Boolean(
        task?.pendingCreate ||
        String(task?._id || '').startsWith('pending-create:')
    )

const SprintSection = ({
    sprint,
    isBacklogSprint,
    sprintMutationKey,
    handleCompleteSprint,
    requestDeleteSprint,
    requestCompleteSprint,
    handleStartSprint,
    hasAtLeastOneTask,
    hasAnyActiveSprint,
    openCreateSprintModal,
    openEditSprintModal,
    isSprintMenuOpen,
    setOpenSprintMenuId,
    sprintHScrollRefs,
    syncHorizontalScroll,
    epicsForBoard,
    sprintTaskBuckets,
    epicGridStyle,
    EPIC_CREATE_DRAFT_ID,
    onTaskClick,
    projectId,
    dispatch,
    filteredAssignees,
    noopSetLocalTaskList,
    refreshTasks,
    createTaskStates,
    initialCreateTask,
    setCreateTaskForLane,
    openCreateTaskInLane,
    createTaskFromSprintLane,
    isIssuesSprint,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false)
    const isCompletedSprint = !!sprint?.raw?.isComplete
    const formatSprintDate = (value) => {
        if (value == null || value === '') return ''
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return ''
        return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
            .format(d)
            .toUpperCase()
    }
    const completedDateText = formatSprintDate(
        sprint?.raw?.completedAt ??
            sprint?.raw?.completed_at ??
            sprint?.raw?.completedOn ??
            sprint?.raw?.completedDate
    )
    const completedBadgeText = completedDateText
        ? `COMPLETED - ${completedDateText}`
        : 'COMPLETED'

    const sprintStartText = formatSprintDate(
        sprint?.raw?.start ?? sprint?.raw?.startDate
    )
    const sprintEndText = formatSprintDate(
        sprint?.raw?.end ?? sprint?.raw?.endDate
    )
    const sprintDateRangeText =
        sprintStartText && sprintEndText
            ? `${sprintStartText} – ${sprintEndText}`
            : sprintStartText || sprintEndText

    return (
        <div
            key={sprint?.id}
            className={`${sectionStyles.sprintSection}${isCompletedSprint ? ` ${sectionStyles.sprintSectionCompleted}` : ''}`}
        >
            <div className={sectionStyles.sprintHeader}>
                <div className={sectionStyles.sprintTitleRow}>
                    <span className={sectionStyles.sprintTitle}>
                        {sprint.title}
                    </span>
                    {!isBacklogSprint(sprint) && sprintDateRangeText && (
                        <span className={sectionStyles.sprintDateRange}>
                            {sprintDateRangeText}
                        </span>
                    )}
                    {isCompletedSprint && (
                        <span className={sectionStyles.sprintCompletedMeta}>
                            {completedBadgeText}
                        </span>
                    )}
                    {sprint.raw?.isActive && (
                        <span className={sectionStyles.sprintActiveBadge}>
                            Active
                        </span>
                    )}
                </div>
                <div className={sectionStyles.sprintHeaderActions}>
                    {!isBacklogSprint(sprint) &&
                        !sprint.raw?.isComplete &&
                        sprint.raw?.isActive && (
                            <Button
                                type="primary"
                                size="small"
                                className={sectionStyles.sprintHeaderActionBtn}
                                loading={
                                    sprintMutationKey ===
                                    `complete:${sprint?.id}`
                                }
                                disabled={
                                    sprintMutationKey !== null &&
                                    sprintMutationKey !==
                                        `complete:${sprint?.id}`
                                }
                                onClick={(e) => {
                                    e.preventDefault()
                                    if (requestCompleteSprint) {
                                        requestCompleteSprint(sprint)
                                    } else {
                                        handleCompleteSprint(sprint)
                                    }
                                }}
                            >
                                Complete Sprint
                            </Button>
                        )}
                    {!isBacklogSprint(sprint) &&
                        !sprint.raw?.isComplete &&
                        !sprint.raw?.isActive &&
                        hasAtLeastOneTask && (
                            <Button
                                type="primary"
                                size="small"
                                className={sectionStyles.sprintHeaderActionBtn}
                                loading={
                                    sprintMutationKey === `start:${sprint?.id}`
                                }
                                disabled={
                                    (sprintMutationKey !== null &&
                                        sprintMutationKey !==
                                            `start:${sprint?.id}`) ||
                                    hasAnyActiveSprint
                                }
                                onClick={(e) => {
                                    e.preventDefault()
                                    handleStartSprint(sprint)
                                }}
                            >
                                Start Sprint
                            </Button>
                        )}
                    {isBacklogSprint(sprint) && (
                        <Button
                            type="default"
                            size="small"
                            className={sectionStyles.addSprintButton}
                            onClick={(e) => {
                                e.preventDefault()
                                openCreateSprintModal()
                            }}
                        >
                            Add Sprint
                            <span
                                className={sectionStyles.addSprintIconCircle}
                                aria-hidden
                            >
                                <Icon icon="mdi:plus" width={11} height={11} />
                            </span>
                        </Button>
                    )}
                    {!isBacklogSprint(sprint) && !isCompletedSprint && (
                        <Dropdown
                            overlayClassName={sectionStyles.sprintMenuDropdown}
                            trigger={['click']}
                            open={isSprintMenuOpen}
                            onOpenChange={(open) => {
                                setOpenSprintMenuId?.(open ? sprint?.id : null)
                            }}
                            menu={{
                                items: [
                                    { key: 'edit', label: 'Edit sprint' },
                                    {
                                        key: 'delete',
                                        label: 'Delete sprint',
                                        danger: true,
                                    },
                                ],
                                onClick: ({ key }) => {
                                    setOpenSprintMenuId?.(null)
                                    if (key === 'edit') {
                                        openEditSprintModal(sprint)
                                        return
                                    }
                                    if (key === 'delete') {
                                        requestDeleteSprint?.(sprint)
                                    }
                                },
                            }}
                        >
                            <button
                                type="button"
                                className={sectionStyles.sprintMenuBtn}
                                title="Sprint options"
                                aria-label="Sprint options"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Icon
                                    icon="mdi:dots-vertical"
                                    width={16}
                                    height={16}
                                />
                            </button>
                        </Dropdown>
                    )}
                    <button
                        type="button"
                        className={`${sectionStyles.sprintCollapseBtn}${
                            isCollapsed
                                ? ` ${sectionStyles.sprintCollapseBtnCollapsed}`
                                : ''
                        }`}
                        title={
                            isCollapsed ? 'Expand sprint' : 'Collapse sprint'
                        }
                        aria-label={
                            isCollapsed ? 'Expand sprint' : 'Collapse sprint'
                        }
                        aria-expanded={!isCollapsed}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setIsCollapsed((prev) => !prev)
                        }}
                    >
                        <Icon
                            icon="mdi:chevron-down"
                            width={18}
                            height={18}
                            className={sectionStyles.sprintCollapseIcon}
                        />
                    </button>
                </div>
            </div>
            <div
                className={`${sectionStyles.sprintBody}${
                    isCollapsed ? ` ${sectionStyles.sprintBodyCollapsed}` : ''
                }`}
            >
                <div className={sectionStyles.sprintBodyInner}>
                    <div className={sectionStyles.sprintScrollRow}>
                        <div
                            ref={(el) => {
                                if (el)
                                    sprintHScrollRefs.current[sprint?.id] = el
                                else
                                    delete sprintHScrollRefs.current[sprint?.id]
                            }}
                            className={sectionStyles.sprintEpicScroll}
                            onScroll={(e) =>
                                syncHorizontalScroll(e.currentTarget)
                            }
                        >
                            <div
                                className={sectionStyles.sprintEpicGrid}
                                style={epicGridStyle}
                            >
                                {epicsForBoard.map((epic) => {
                                    const droppableId = `${sprint?.id}::${epic.id}`
                                    const list =
                                        sprintTaskBuckets?.[epic.id] || []
                                    const isCreateDraftCol =
                                        epic.id === EPIC_CREATE_DRAFT_ID
                                    const laneKey = `${sprint?.id}::${epic.id}`
                                    const laneCreateTask =
                                        createTaskStates?.[laneKey] ||
                                        initialCreateTask

                                    return (
                                        <Droppable
                                            key={droppableId}
                                            droppableId={droppableId}
                                            type="task"
                                            isDropDisabled={
                                                isCreateDraftCol ||
                                                isCompletedSprint
                                            }
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`${sectionStyles.taskLane}${snapshot.isDraggingOver ? ` ${sectionStyles.taskLaneOver}` : ''}`}
                                                >
                                                    {list.map(
                                                        (task, taskIndex) => {
                                                            const isPendingCreate =
                                                                isPendingCreateTask(
                                                                    task
                                                                )
                                                            return (
                                                                <Draggable
                                                                    key={
                                                                        task?._id ||
                                                                        `${droppableId}-${taskIndex}`
                                                                    }
                                                                    draggableId={`${
                                                                        task?._id ||
                                                                        `${droppableId}-${taskIndex}`
                                                                    }`}
                                                                    index={
                                                                        taskIndex
                                                                    }
                                                                    isDragDisabled={
                                                                        isCompletedSprint ||
                                                                        isPendingCreate
                                                                    }
                                                                >
                                                                    {(
                                                                        taskProvided
                                                                    ) => (
                                                                        <div
                                                                            ref={
                                                                                taskProvided.innerRef
                                                                            }
                                                                            {...taskProvided.draggableProps}
                                                                            {...taskProvided.dragHandleProps}
                                                                            className={
                                                                                sectionStyles.taskCard
                                                                            }
                                                                        >
                                                                            <TaskCard
                                                                                task={
                                                                                    task
                                                                                }
                                                                                onTaskClick={
                                                                                    onTaskClick
                                                                                }
                                                                                containerClassName={`${sectionStyles.sprintTaskCard}${
                                                                                    isCompletedSprint
                                                                                        ? ` ${sectionStyles.completedTaskCard}`
                                                                                        : ''
                                                                                }`}
                                                                                readOnlyActions={
                                                                                    isCompletedSprint ||
                                                                                    isPendingCreate
                                                                                }
                                                                                singleLineTitle
                                                                                projectId={
                                                                                    projectId
                                                                                }
                                                                                dispatch={
                                                                                    dispatch
                                                                                }
                                                                                filteredAssignees={
                                                                                    filteredAssignees
                                                                                }
                                                                                setLocalTaskList={
                                                                                    noopSetLocalTaskList
                                                                                }
                                                                                taskListKind="main"
                                                                                refreshTasks={
                                                                                    refreshTasks
                                                                                }
                                                                                taskRouteView="sprint"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            )
                                                        }
                                                    )}
                                                    {laneCreateTask?.open &&
                                                        !isCreateDraftCol &&
                                                        !isCompletedSprint && (
                                                            <CreateTaskCard
                                                                createTask={
                                                                    laneCreateTask
                                                                }
                                                                setCreateTask={setCreateTaskForLane(
                                                                    sprint?.id,
                                                                    epic?.id
                                                                )}
                                                                filteredAssignees={
                                                                    filteredAssignees
                                                                }
                                                                createTaskFromBoard={(
                                                                    taskName,
                                                                    options
                                                                ) =>
                                                                    createTaskFromSprintLane(
                                                                        taskName,
                                                                        sprint,
                                                                        epic,
                                                                        options
                                                                    )
                                                                }
                                                                initialCreateTask={
                                                                    initialCreateTask
                                                                }
                                                                projectId={
                                                                    projectId
                                                                }
                                                                className={`${sectionStyles.sprintCreateTaskCard} ${sectionStyles.taskCreateCard}`}
                                                                contentClassName={
                                                                    sectionStyles.sprintCreateTaskCardContent
                                                                }
                                                                headerClassName={
                                                                    sectionStyles.sprintCreateTaskCardHeader
                                                                }
                                                                actionsClassName={
                                                                    sectionStyles.sprintCreateTaskCardActions
                                                                }
                                                                inputClassName={
                                                                    sectionStyles.sprintCreateTaskInput
                                                                }
                                                                taskListKind="main"
                                                                taskType={
                                                                    isIssuesSprint(
                                                                        sprint
                                                                    )
                                                                        ? 'Bug'
                                                                        : 'Task'
                                                                }
                                                                typeDisabled={isIssuesSprint(
                                                                    sprint
                                                                )}
                                                            />
                                                        )}
                                                    {!isCreateDraftCol &&
                                                        !isCompletedSprint &&
                                                        !laneCreateTask?.open && (
                                                            <div
                                                                className={
                                                                    sectionStyles.taskLaneAddSlot
                                                                }
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()
                                                                    openCreateTaskInLane(
                                                                        sprint?.id,
                                                                        epic?.id
                                                                    )
                                                                }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={
                                                                        sectionStyles.taskLaneAddBtn
                                                                    }
                                                                    aria-label="Add task"
                                                                    title="Add task"
                                                                    onMouseDown={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <Icon
                                                                        icon="mdi:plus"
                                                                        width={
                                                                            16
                                                                        }
                                                                        height={
                                                                            16
                                                                        }
                                                                    />
                                                                </button>
                                                            </div>
                                                        )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

/**
 * Every sprint row renders an epic-wide grid of task cards, so re-rendering all
 * of them whenever any board state changed was the main source of sprint-board
 * lag. Props are now per-sprint values (bucket, menu-open flag, task-count flag)
 * so a section only re-renders when its own data moves.
 */
export default React.memo(SprintSection)
